import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import enrolLoyaltyChooseDoubleRewards from '../actions/enrolLoyaltyChooseDoubleRewards'
import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import useLoyaltyChooseDoubleRewardsEnrolmentState from '../hooks/useLoyaltyChooseDoubleRewardsEnrolmentState'
import type { ChooseDoubleRewardsChoice } from '../utils/loyaltyChooseDoubleRewardsState'
import LoyaltyRewardChoiceGradientHeader from './LoyaltyRewardChoiceGradientHeader'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'

function renderEmphasis(chunks: ReactNode) {
  return <strong key="emphasis"><em>{chunks}</em></strong>
}

interface Props {
  choice: ChooseDoubleRewardsChoice
  onBack: () => void
}

function LoyaltyConfirmRewardChoiceContent({
  choice,
  onBack,
}: Props) {
  const { track } = useLoyaltyChooseDoubleRewardsTracking('choice_modal')
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const { endsAt, canEnrol, memberState } = useLoyaltyChooseDoubleRewards(choice)
  const { pending, error, succeeded } = useLoyaltyChooseDoubleRewardsEnrolmentState(choice)
  const modal = useModalElementContext()

  useEffect(() => {
    if (succeeded && memberState.status === 'enrolled') {
      track(`choice_confirmed_${memberState.choice}`)
      modal.resolve()
    }
  }, [succeeded, memberState, modal, track])

  const loyaltyProgramName = getLoyaltyProgramDisplayName()

  const back = useCallback(() => {
    if (!pending) onBack()
  }, [onBack, pending])

  const dismiss = useCallback(() => {
    if (!pending) modal.resolve()
  }, [modal, pending])

  const confirm = useCallback(() => {
    if (!pending && canEnrol) dispatch(enrolLoyaltyChooseDoubleRewards(choice))
  }, [choice, dispatch, pending, canEnrol])

  const pointsTitle = intl.formatMessage({
    id: 'dzVPhRpiojP-cZ5QZ0Yd',
    defaultMessage: '<emphasis>Double {loyaltyProgramName} Points.</emphasis> Great choice!',
    description: 'Confirmation modal title when the member chose double loyalty program points',
  }, { loyaltyProgramName, emphasis: renderEmphasis })

  const statusTitle = intl.formatMessage({
    id: 'n9yq1gD_L1iAKrxXaqhH',
    defaultMessage: '<emphasis>Double Status Credits.</emphasis> Great choice!',
    description: 'Confirmation modal title when the member chose double Status Credits',
  }, { emphasis: renderEmphasis })

  const confirmLabel = intl.formatMessage({
    id: 'f9iA6o8rBNk4sQcQHkH8',
    defaultMessage: 'Confirm and activate',
    description: 'Button confirming and enrolling the selected double reward campaign option',
  })

  const backLabel = intl.formatMessage({
    id: 'ID5ytproTrU6CKh-VYl4',
    defaultMessage: 'Go back',
    description: 'Button to return from double rewards confirmation to the previous screen',
  })

  const datedPointsConfirmation = endsAt && intl.formatMessage({
    id: 'L66Z301rESnUeqck-cHA',
    defaultMessage: 'Confirm your choice to earn double {loyaltyProgramName} Points on eligible bookings you make by {date}. Once confirmed, you can’t change your reward during this campaign.',
    description: 'Confirmation text explaining the points reward and irreversible campaign selection',
  }, {
    loyaltyProgramName,
    date: <FormatDateTime value={endsAt} day="numeric" month="long" year="numeric" />,
  })

  const undatedPointsConfirmation = intl.formatMessage({
    id: 'slG_h2Qpdgv3x9qIeFLP',
    defaultMessage: 'Confirm your choice to earn double {loyaltyProgramName} Points on eligible bookings during this campaign. Once confirmed, you can’t change your reward during this campaign.',
    description: 'Points reward confirmation when the double rewards campaign end date is unavailable',
  }, { loyaltyProgramName })

  const datedStatusConfirmation = endsAt && intl.formatMessage({
    id: '9V3AtvfG_Z_xu9fHNFC0',
    defaultMessage: 'Confirm your choice to earn double Status Credits on eligible bookings you make by {date}. Once confirmed, you can’t change your reward during this campaign.',
    description: 'Confirmation text explaining the status credits reward and irreversible campaign selection',
  }, {
    date: <FormatDateTime value={endsAt} day="numeric" month="long" year="numeric" />,
  })

  const undatedStatusConfirmation = intl.formatMessage({
    id: 'zcuaT5CZOMbVKMn4M9ks',
    defaultMessage: 'Confirm your choice to earn double Status Credits on eligible bookings during this campaign. Once confirmed, you can’t change your reward during this campaign.',
    description: 'Status credits reward confirmation when the double rewards campaign end date is unavailable',
  })

  const pointsConfirmation = endsAt ? datedPointsConfirmation : undatedPointsConfirmation
  const statusConfirmation = endsAt ? datedStatusConfirmation : undatedStatusConfirmation

  const errorMessage = intl.formatMessage({
    id: 'dbF1B9MfP_uzA-UxiKR1',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Error message when the double rewards enrolment request cannot be completed',
  })

  const primaryActionProps = useMemo(() => ({
    children: confirmLabel,
    disabled: pending || !canEnrol,
    onClick: confirm,
  }), [confirm, confirmLabel, pending, canEnrol])

  const secondaryActionProps = useMemo(() => ({
    children: backLabel,
    disabled: pending,
    onClick: back,
  }), [back, backLabel, pending])

  return <>
    <LoyaltyRewardChoiceGradientHeader
      tier={tier}
      title={choice === 'points' ? pointsTitle : statusTitle}
      dismissible={!pending}
      onClose={dismiss}
    />
    <ModalBody>
      <div>
        <ModalContent>
          <Group direction="vertical" gap={16}>
            {choice === 'points' && <BodyText variant="medium">
              {pointsConfirmation}
            </BodyText>}
            {choice === 'statusCredits' && <BodyText variant="medium">
              {statusConfirmation}
            </BodyText>}
            {error && <MessageBanner kind="critical">
              {errorMessage}
            </MessageBanner>}
          </Group>
        </ModalContent>
      </div>
    </ModalBody>
    <ModalFooter
      primaryActionProps={primaryActionProps}
      secondaryActionProps={secondaryActionProps}
    />
  </>
}

export default LoyaltyConfirmRewardChoiceContent
